import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Phone, Mail, MapPin, Facebook, Instagram, Globe, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const SharedFooter = ({ content = {} }) => {
  const logoUrl = content.site_logo || "https://customer-assets.emergentagent.com/job_rustic-retreat-7/artifacts/gtmkak5t_5556a.png";
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/social-links`).then(res => setSocialLinks(res.data)).catch(() => {});
  }, []);

  const getSocialIcon = (name) => {
    switch (name) {
      case 'instagram': return <Instagram className="w-5 h-5 text-white" />;
      case 'facebook': return <Facebook className="w-5 h-5 text-white" />;
      case 'tiktok': return <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>;
      case 'google': return <Globe className="w-5 h-5 text-white" />;
      case 'booking': return <ExternalLink className="w-5 h-5 text-white" />;
      default: return <Globe className="w-5 h-5 text-white" />;
    }
  };
  
  return (
    <footer className="bg-forest-dark" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="md:col-span-1">
            <img 
              src={logoUrl}
              alt="VIP Village" 
              className="h-20 w-auto object-contain mb-4"
            />
            <p className="font-body text-white/60 text-sm leading-relaxed">
              Agroturystyka VIP Village - komfortowy wypoczynek w sercu natury Świętokrzyskiego.
            </p>
          </div>
          
          {/* Navigation */}
          <div>
            <h4 className="font-heading text-lg text-white mb-4">Nawigacja</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="font-body text-sm text-white/70 hover:text-terracotta transition">
                  Strona główna
                </Link>
              </li>
              <li>
                <a href="/#offer" className="font-body text-sm text-white/70 hover:text-terracotta transition">
                  Oferta zakwaterowania
                </a>
              </li>
              <li>
                <Link to="/spa" className="font-body text-sm text-white/70 hover:text-terracotta transition">
                  Balia • Basen • Sauna
                </Link>
              </li>
              <li>
                <a href="/#gallery" className="font-body text-sm text-white/70 hover:text-terracotta transition">
                  Galeria
                </a>
              </li>
              <li>
                <Link to="/regulamin" className="font-body text-sm text-white/70 hover:text-terracotta transition">
                  Regulamin
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg text-white mb-4">Kontakt</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" />
                <span className="font-body text-sm text-white/70">
                  {content.address || "Miedzierza 110, 26-212 Smyków"}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-terracotta flex-shrink-0" />
                <a 
                  href={`tel:${content.phone || '+48605737885'}`}
                  className="font-body text-sm text-white/70 hover:text-white transition"
                >
                  {content.phone || "+48 605 737 885"}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-terracotta flex-shrink-0" />
                <a 
                  href={`mailto:${content.email || 'agromiedzierza@gmail.com'}`}
                  className="font-body text-sm text-white/70 hover:text-white transition"
                >
                  {content.email || "agromiedzierza@gmail.com"}
                </a>
              </li>
            </ul>
          </div>
          
          {/* Hours & Social */}
          <div>
            <h4 className="font-heading text-lg text-white mb-4">Godziny przyjęć</h4>
            <p className="font-body text-sm text-white/70 mb-2">
              <strong className="text-white">Zameldowanie:</strong> od 14:00
            </p>
            <p className="font-body text-sm text-white/70 mb-4">
              <strong className="text-white">Wymeldowanie:</strong> do 10:00
            </p>
            
            <h4 className="font-heading text-lg text-white mb-3 mt-6">Znajdź nas</h4>
            <div className="flex gap-3">
              {socialLinks.filter(link => link.enabled && link.url).map((link) => (
                <a 
                  key={link.id}
                  href={link.url}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-terracotta transition"
                  title={link.name.charAt(0).toUpperCase() + link.name.slice(1)}
                >
                  {getSocialIcon(link.name)}
                </a>
              ))}
              {/* Fallback if no social links */}
              {socialLinks.filter(link => link.enabled && link.url).length === 0 && (
                <>
                  <a 
                    href="https://www.instagram.com/vipvillage" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-terracotta transition"
                  >
                    <Instagram className="w-5 h-5 text-white" />
                  </a>
                  <a 
                    href="https://www.facebook.com/VIP-Village-1372695046171760/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-terracotta transition"
                  >
                    <Facebook className="w-5 h-5 text-white" />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row justify-center items-center gap-4">
          <p className="font-body text-sm text-white/40">
            © {new Date().getFullYear()} VIP Village Agromiedzierza. Wszelkie prawa zastrzeżone.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SharedFooter;
